import { Component, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { NotificacionesStore } from '../../services/notificaciones.store';
import { NotificacionesCatalogosStore } from '../../services/notificaciones-catalogos.store';
import { NotificacionPreviewComponent } from '../notificacion-preview/notificacion-preview.component';
import { MultiSelectCambio, MultiSelectDropdownComponent, MultiSelectOpcion } from '../multi-select-dropdown/multi-select-dropdown.component';
import { fechaFuturaArgentinaValidator, textoBotonValidator, urlExternaValidator } from '../../custom-validators';
import {
  CampoEnlace,
  CrearNotificacionInstitucionalRequest,
  EnlaceNotificacion,
  LocalidadNotificacion,
  MAX_TEXTO_BOTON_ENLACE,
  MAX_URL_ENLACE,
  NotificacionInstitucional,
  SeccionEnlace,
  Sexo,
  TEXTO_BOTON_ENLACE_SUGERIDO,
  TipoAudiencia,
  TipoEnlace,
  TipoEntrega,
  datetimeLocalAIsoArgentina,
  fechaHoraArgentinaActual,
  formatearFechaHoraArgentina,
  isoADatetimeLocalArgentina
} from '../../models/notificacion-institucional.model';

export type ModoNotificacionNueva = 'alta' | 'editar' | 'consulta';

// Pantalla completa que maneja alta/editar/consulta en un solo componente —
// ver docs/specs/abmc-notificacion-institucional.md §3.3, corrección
// post-implementación #5 (reemplaza al viejo modal NotificacionFormComponent,
// que se borró).
@Component({
  selector: 'app-notificacion-nueva',
  standalone: true,
  imports: [ReactiveFormsModule, RouterLink, NotificacionPreviewComponent, MultiSelectDropdownComponent],
  templateUrl: './notificacion-nueva.component.html',
  styleUrl: './notificacion-nueva.component.scss'
})
export class NotificacionNuevaComponent {
  private readonly fb = inject(FormBuilder);
  private readonly store = inject(NotificacionesStore);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  readonly catalogos = inject(NotificacionesCatalogosStore);

  // Param opcional de la ruta `notificaciones/:id/editar` — se lee una sola
  // vez al entrar a la pantalla (snapshot, sin necesidad de reactividad: la
  // pantalla no cambia de una notificación a otra sin recargar).
  readonly id = this.leerIdDeRuta();

  // `editable` viene del response del backend (regla server-side, ver §6). En
  // modo alta (id===null) el default no importa. En editar/consulta arranca
  // en `false` a propósito: mientras no resuelve obtener(), es más seguro que
  // los botones de Guardar/Eliminar queden ocultos (como si fuera consulta)
  // a que aparezcan un instante y después se saquen si la notificación
  // resulta no editable.
  readonly editable = signal(false);
  readonly modo = computed<ModoNotificacionNueva>(() => {
    if (this.id === null) return 'alta';
    return this.editable() ? 'editar' : 'consulta';
  });

  readonly tituloPantalla = computed(() => {
    switch (this.modo()) {
      case 'editar': return 'Editar notificación';
      case 'consulta': return 'Consultar notificación';
      default: return 'Nueva notificación';
    }
  });
  readonly textoAyuda = computed(() => {
    switch (this.modo()) {
      case 'editar': return 'Modificá la audiencia, el mensaje o el envío de esta notificación programada.';
      case 'consulta': return 'Estás viendo el detalle de esta notificación. Es de solo lectura.';
      default: return 'Definí la audiencia, el mensaje y cuándo se envía.';
    }
  });

  readonly guardando = this.store.guardando;
  readonly fechaMinima = fechaHoraArgentinaActual();
  // "YYYY-MM-DD" — solo la parte de fecha, para el [min] del <input type="date">.
  readonly fechaMinimaParte = this.fechaMinima.split('T')[0];

  readonly form: FormGroup = this.fb.group({
    nombreInterno: ['', Validators.required],
    titulo: ['', [Validators.required, Validators.maxLength(120)]],
    mensajeCorto: ['', [Validators.required, Validators.maxLength(180)]],
    mensajeAmpliado: ['', [Validators.required, Validators.maxLength(500)]],
    tipoEntrega: ['INMEDIATA' as TipoEntrega, Validators.required],
    fechaProgramacion: [''],
    tipoAudiencia: ['TODOS' as TipoAudiencia, Validators.required],
    sexo: ['AMBOS' as Sexo],
    // Reemplaza al viejo multi-select de rango etario (corrección #7 de la
    // spec) — rango numérico libre, sin catálogo ni patrón todos/ids.
    edadDesde: [null as number | null],
    edadHasta: [null as number | null],
    // Enlace opcional del pop-up. Validadores condicionales según el tipo
    // (ver actualizarValidadoresEnlace); URL y sección van en controles
    // separados aunque viajen los dos en `enlace.destino`.
    enlaceTipo: ['SIN_ENLACE' as TipoEnlace],
    enlaceUrl: [''],
    enlaceSeccion: [''],
    enlaceTextoBoton: ['']
  });

  readonly maxUrlEnlace = MAX_URL_ENLACE;
  readonly maxTextoBotonEnlace = MAX_TEXTO_BOTON_ENLACE;

  private readonly tipoEnlaceSig = toSignal(this.form.get('enlaceTipo')!.valueChanges, { initialValue: 'SIN_ENLACE' as TipoEnlace });
  private readonly textoBotonEnlaceSig = toSignal(this.form.get('enlaceTextoBoton')!.valueChanges, { initialValue: '' });
  private readonly urlEnlaceSig = toSignal(this.form.get('enlaceUrl')!.valueChanges, { initialValue: '' });
  private readonly seccionEnlaceSig = toSignal(this.form.get('enlaceSeccion')!.valueChanges, { initialValue: '' });
  readonly tipoEnlace = computed(() => this.tipoEnlaceSig());
  readonly previewUrlEnlace = computed(() => this.urlEnlaceSig() ?? '');
  readonly previewSeccionNombre = computed(() => {
    const id = this.seccionEnlaceSig();
    return this.seccionOpciones().find(s => s.id === id)?.nombre ?? '';
  });
  readonly tieneEnlace = computed(() => this.tipoEnlaceSig() !== 'SIN_ENLACE');
  readonly largoTextoBotonEnlace = computed(() => (this.textoBotonEnlaceSig() ?? '').length);
  // null = el pop-up no lleva botón.
  readonly previewTextoBotonEnlace = computed(() => (this.tieneEnlace() ? this.textoBotonEnlaceSig() ?? '' : null));

  // Error de validación del enlace devuelto por backend (400), ya asociado al
  // campo por el store.
  readonly errorEnlace = this.store.errorEnlace;

  // Solo las secciones del catálogo. Si la notificación que se está viendo
  // apunta a una sección que Sistemas dio de baja después, se agrega como
  // opción para que el select no quede en blanco (el back la va a rechazar
  // si se intenta guardar así).
  private readonly seccionHidratada = signal<string | null>(null);
  readonly seccionOpciones = computed<SeccionEnlace[]>(() => {
    const catalogo = this.catalogos.seccionesEnlace();
    const id = this.seccionHidratada();
    if (!id || catalogo.some(s => s.id === id)) return catalogo;
    return [...catalogo, { id, nombre: `${id} (no habilitada)` }];
  });

  // "Todos" es un estado explícito, no inferido de la selección — ver
  // docs/specs/abmc-notificacion-institucional.md §9.9. `ids` solo importa
  // cuando `todos=false`; si queda en false con ids=[] es el estado
  // ambiguo que bloquea el submit (ver coberturaInvalida). Provincia y
  // Localidad usan el mismo patrón (ver §5) — quedaron fuera del FormGroup,
  // como Cobertura, porque son multi-select (Rango etario ya no: pasó a
  // edadDesde/edadHasta dentro del FormGroup, ver corrección #7).
  // Cobertura/Provincia/Localidad operan por id de catálogo — el contrato
  // real de la notificación (corrección #9, ver
  // docs/specs/abmc-notificacion-institucional.md §9.10) manda {id, nombre}
  // para los tres, así que hidratar es directo (ver aplicarDatosNotificacion),
  // sin necesidad de matchear por nombre contra ningún catálogo.
  readonly coberturaTodos = signal(true);
  readonly coberturaIds = signal<number[]>([]);
  readonly provinciaTodos = signal(true);
  readonly provinciaIds = signal<number[]>([]);
  readonly localidadTodos = signal(true);
  readonly localidadIds = signal<number[]>([]);

  // Puede haber localidades homónimas en distintas provincias (ej. "Merlo"
  // en Córdoba y San Luis) — si hay más de una provincia elegida a la vez,
  // se aclara entre paréntesis para no mostrar dos filas indistinguibles.
  readonly localidadOpciones = computed<MultiSelectOpcion<number>[]>(() => {
    const ambiguo = this.provinciaIds().length > 1;
    return this.catalogos.localidades().map(l => ({
      id: l.id,
      nombre: ambiguo ? `${l.nombre} (${l.provinciaNombre})` : l.nombre
    }));
  });

  readonly submitted = signal(false);

  // Dato real de envío (distinto de fechaParte/horaParte, que son la
  // programación editable) — solo lectura, se muestra si ya se disparó. No
  // hay estimación en vivo antes de eso, `cantidadDestinatarios` es un
  // snapshot que calcula backend recién en el momento del envío real (ver
  // docs/specs/abmc-notificacion-institucional.md §9.11, pedido pendiente).
  readonly fechaEnvioReal = signal<string | null>(null);
  readonly cantidadDestinatarios = signal<number | null>(null);

  // Campos "vista" del <input type="datetime-local"> original, separados en
  // fecha/hora nativos (ver docs/mockups/NewNotification.dc.html). No son
  // parte del FormGroup — arman el string "YYYY-MM-DDTHH:mm" y lo escriben en
  // el control real `fechaProgramacion`, que sigue siendo la fuente de verdad
  // para validación (fechaFuturaArgentinaValidator).
  readonly fechaParte = signal('');
  readonly horaParte = signal('');

  // `titulo` es el título visible de la notificación (push y pop-up) — campo
  // propio, separado de `nombreInterno` (que quedó solo interno, ver
  // corrección post-implementación #7 de la spec, revierte la #6).
  private readonly tituloSig = toSignal(this.form.get('titulo')!.valueChanges, { initialValue: '' });
  private readonly mensajeCortoSig = toSignal(this.form.get('mensajeCorto')!.valueChanges, { initialValue: '' });
  private readonly mensajeAmpliadoSig = toSignal(this.form.get('mensajeAmpliado')!.valueChanges, { initialValue: '' });
  readonly previewTitulo = computed(() => this.tituloSig() ?? '');
  readonly previewCorto = computed(() => this.mensajeCortoSig() ?? '');
  readonly previewAmpliado = computed(() => this.mensajeAmpliadoSig() ?? '');

  // Edad desde/hasta (corrección #7): sin catálogo ni patrón todos/ids, solo
  // valida que, si los dos vienen cargados, edadDesde <= edadHasta (mismo
  // criterio que el 400 del backend, bloqueado antes de enviar).
  private readonly edadDesdeSig = toSignal(this.form.get('edadDesde')!.valueChanges, { initialValue: null as number | null });
  private readonly edadHastaSig = toSignal(this.form.get('edadHasta')!.valueChanges, { initialValue: null as number | null });
  readonly edadInvalida = computed(() => {
    const desde = this.edadDesdeSig();
    const hasta = this.edadHastaSig();
    return desde !== null && hasta !== null && desde > hasta;
  });

  private readonly tipoEntregaSig = toSignal(this.form.get('tipoEntrega')!.valueChanges, { initialValue: 'INMEDIATA' as TipoEntrega });
  readonly esProgramada = computed(() => this.tipoEntregaSig() === 'PROGRAMADA');
  readonly textoBoton = computed(() => (this.esProgramada() ? 'Revisar y programar' : 'Revisar y enviar'));

  private readonly tipoAudienciaSig = toSignal(this.form.get('tipoAudiencia')!.valueChanges, { initialValue: 'TODOS' as TipoAudiencia });
  readonly esAudienciaFiltrada = computed(() => this.tipoAudienciaSig() === 'FILTROS');

  // Estado ambiguo (§9.9): ni "Todos" ni ninguna opción puntual. Bloquea el submit.
  readonly coberturaInvalida = computed(() => this.esAudienciaFiltrada() && !this.coberturaTodos() && this.coberturaIds().length === 0);
  readonly provinciaInvalida = computed(() => this.esAudienciaFiltrada() && !this.provinciaTodos() && this.provinciaIds().length === 0);
  readonly localidadInvalida = computed(() => this.esAudienciaFiltrada() && !this.localidadTodos() && this.localidadIds().length === 0);

  // Mismo criterio que el early-return de onSubmit() — el botón queda
  // deshabilitado hasta que se cumpla, en vez de dejar que el usuario lo
  // toque y no pase nada.
  private readonly formStatusSig = toSignal(this.form.statusChanges, { initialValue: this.form.status });
  readonly puedeGuardar = computed(() =>
    this.formStatusSig() === 'VALID' &&
    !this.coberturaInvalida() &&
    !this.provinciaInvalida() &&
    !this.localidadInvalida() &&
    !this.edadInvalida()
  );

  constructor() {
    this.catalogos.cargarBase();
    this.catalogos.cargarSeccionesEnlace();
    // Store singleton: no arrastrar el error de un guardado anterior.
    this.store.limpiarErrorEnlace();

    effect(() => this.actualizarValidadorFecha(this.tipoEntregaSig()));

    // Sincrónico (no effect) a propósito: los validadores tienen que estar
    // puestos antes de que el form recalcule su status, si no puedeGuardar
    // queda desfasado. Solo toca validadores, nunca valores — limpiar los
    // campos al cambiar de tipo lo hace onTipoEnlaceCambio (interacción del
    // usuario), así la hidratación en editar/consulta no los pisa.
    this.form.get('enlaceTipo')!.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((tipo: TipoEnlace) => this.actualizarValidadoresEnlace(tipo));

    // El error de backend deja de aplicar en cuanto el usuario toca el enlace.
    for (const campo of ['enlaceUrl', 'enlaceSeccion', 'enlaceTextoBoton']) {
      this.form.get(campo)!.valueChanges
        .pipe(takeUntilDestroyed())
        .subscribe(() => {
          if (this.errorEnlace()) this.store.limpiarErrorEnlace();
        });
    }

    // Regla dura del backend (§9.9): si localidadIds no está vacío,
    // provinciaIds tampoco puede estarlo (400 localidadSinProvincia). Cuando
    // Provincia vuelve a "Todos" (o queda en el estado ambiguo, ids=[]),
    // resetea Localidad también, y siempre re-dispara la carga de
    // localidades para el conjunto actual de provincias (cargarLocalidades
    // ya devuelve [] sin llamar si el array viene vacío).
    effect(() => {
      const provinciaIds = this.provinciaIds();
      if (provinciaIds.length === 0) {
        this.localidadTodos.set(true);
        this.localidadIds.set([]);
        this.catalogos.cargarLocalidades([]);
        return;
      }
      // cargarLocalidades necesita nombre además de id (para taggear cada
      // localidad con su provincia) — si el catálogo de provincias todavía
      // no cargó, esperar al próximo disparo en vez de pedir con nombres
      // incompletos.
      const provinciasSeleccionadas = this.catalogos.provincias().filter(p => provinciaIds.includes(p.id));
      if (provinciasSeleccionadas.length !== provinciaIds.length) return;
      this.catalogos.cargarLocalidades(provinciasSeleccionadas);
    }, { allowSignalWrites: true });

    if (this.id !== null) {
      this.store.obtener(this.id).subscribe(n => {
        // store.obtener() ya mostró el error (ej. 404 "No se encontró la
        // notificación") — si no llegó nada, no tiene sentido dejar la
        // pantalla en blanco esperando que el usuario adivine qué pasó.
        if (n) this.hidratarDesde(n); else this.router.navigate(['/notificaciones']);
      });
    }
  }

  private leerIdDeRuta(): number | null {
    const raw = this.route.snapshot.paramMap.get('id');
    return raw !== null ? Number(raw) : null;
  }

  // Hidrata el form y todos los signals de audiencia a partir del detalle
  // (copiado del ngOnInit que tenía notificacion-form.component.ts, ver
  // docs/specs/abmc-notificacion-institucional.md §3.3 corrección #5).
  private hidratarDesde(n: NotificacionInstitucional): void {
    // editable se setea primero y el disable queda en el finally: si el
    // contrato vuelve a sorprender (como pasó con provinciaIds/localidadIds,
    // que ya no existen) y algo de acá abajo explota, una notificación no
    // editable no puede quedar con el form habilitado por accidente.
    this.editable.set(n.editable);
    try {
      this.aplicarDatosNotificacion(n);
    } catch (err) {
      console.error('Error hidratando el detalle de la notificación', err);
    } finally {
      if (this.modo() === 'consulta') {
        this.form.disable();
      }
    }
  }

  private aplicarDatosNotificacion(n: NotificacionInstitucional): void {
    const fechaProgramacionLocal = n.fechaProgramacion ? isoADatetimeLocalArgentina(n.fechaProgramacion) : '';
    // `?? SIN_ENLACE` por las dudas: backend garantiza que siempre viene.
    const enlace = n.enlace ?? { tipo: 'SIN_ENLACE', destino: null, textoBoton: null };
    this.seccionHidratada.set(enlace.tipo === 'SECCION_APP' ? enlace.destino : null);
    this.form.reset({
      nombreInterno: n.nombreInterno,
      titulo: n.titulo,
      mensajeCorto: n.body,
      mensajeAmpliado: n.detalle,
      tipoEntrega: n.tipoEntrega,
      fechaProgramacion: fechaProgramacionLocal,
      tipoAudiencia: n.tipoAudiencia,
      sexo: n.sexo ?? 'AMBOS',
      edadDesde: n.edadDesde,
      edadHasta: n.edadHasta,
      enlaceTipo: enlace.tipo,
      enlaceUrl: enlace.tipo === 'EXTERNO' ? enlace.destino ?? '' : '',
      enlaceSeccion: enlace.tipo === 'SECCION_APP' ? enlace.destino ?? '' : '',
      enlaceTextoBoton: enlace.tipo !== 'SIN_ENLACE' ? enlace.textoBoton ?? '' : ''
    });
    const [fecha, hora] = fechaProgramacionLocal ? fechaProgramacionLocal.split('T') : ['', ''];
    this.fechaParte.set(fecha);
    this.horaParte.set(hora);
    this.fechaEnvioReal.set(n.fechaEnvio);
    this.cantidadDestinatarios.set(n.cantidadDestinatarios ?? null);

    // El response no trae flag `todos` — se deriva acá (§9.9): array vacío
    // siempre fue "Todos" a propósito (el backend nunca permite guardar el
    // estado ambiguo), array con {id,nombre}/pares es "Todos"=false con esos
    // valores puntuales. Cobertura/Provincia/Localidad traen id (corrección
    // #9), así que hidratar es directo, sin matchear contra ningún catálogo.
    this.coberturaTodos.set(n.coberturas.length === 0);
    this.coberturaIds.set(n.coberturas.map(c => c.id));
    this.provinciaTodos.set(n.provincias.length === 0);
    this.provinciaIds.set(n.provincias.map(p => p.id));
    this.localidadTodos.set(n.localidades.length === 0);
    this.localidadIds.set(n.localidades.map(l => l.localidadId));
  }

  esInvalido(campo: string): boolean {
    const ctrl = this.form.get(campo);
    return !!(ctrl?.invalid && (ctrl.dirty || ctrl.touched));
  }

  mensajeErrorEnlaceBackend(campo: CampoEnlace): string | null {
    const error = this.errorEnlace();
    return error?.campo === campo ? error.mensaje : null;
  }

  // Al cambiar de tipo se borran los datos del tipo anterior. El texto del
  // botón también se resetea (al sugerido "Ver más") aunque lo compartan
  // Externo y Sección: es un dato del enlace anterior como los demás.
  onTipoEnlaceCambio(tipo: TipoEnlace): void {
    this.seccionHidratada.set(null);
    this.form.patchValue({
      enlaceUrl: '',
      enlaceSeccion: '',
      enlaceTextoBoton: tipo === 'SIN_ENLACE' ? '' : TEXTO_BOTON_ENLACE_SUGERIDO
    });
    for (const campo of ['enlaceUrl', 'enlaceSeccion', 'enlaceTextoBoton']) {
      const ctrl = this.form.get(campo)!;
      ctrl.markAsPristine();
      ctrl.markAsUntouched();
    }
    this.store.limpiarErrorEnlace();
  }

  formatearFechaEnvio(iso: string): string {
    return formatearFechaHoraArgentina(iso);
  }

  onFechaParteChange(valor: string): void {
    this.fechaParte.set(valor);
    this.actualizarFechaProgramacion();
  }

  onHoraParteChange(valor: string): void {
    this.horaParte.set(valor);
    this.actualizarFechaProgramacion();
  }

  onCancelar(): void {
    this.router.navigate(['/notificaciones']);
  }

  onCoberturaCambio(cambio: MultiSelectCambio): void {
    this.coberturaTodos.set(cambio.todos);
    this.coberturaIds.set(cambio.ids);
  }

  onProvinciaCambio(cambio: MultiSelectCambio): void {
    this.provinciaTodos.set(cambio.todos);
    this.provinciaIds.set(cambio.ids);
  }

  onLocalidadCambio(cambio: MultiSelectCambio): void {
    this.localidadTodos.set(cambio.todos);
    this.localidadIds.set(cambio.ids);
  }

  onEliminar(): void {
    if (this.id === null) return;
    this.store
      .confirmarAccion('Se va a eliminar esta notificación de forma definitiva. ¿Confirmás?', 'Eliminar notificación')
      .then(confirmado => {
        if (!confirmado) return;
        this.store.eliminar(this.id!).subscribe(ok => {
          if (ok) this.router.navigate(['/notificaciones']);
        });
      });
  }

  onSubmit(): void {
    this.submitted.set(true);
    this.form.markAllAsTouched();
    if (this.form.invalid || this.coberturaInvalida() || this.provinciaInvalida() || this.localidadInvalida() || this.edadInvalida()) return;

    const v = this.form.getRawValue();
    const esProgramada = v.tipoEntrega === 'PROGRAMADA';
    const fechaIso = esProgramada ? datetimeLocalAIsoArgentina(v.fechaProgramacion) : null;

    const mensajeConfirmacion = esProgramada
      ? `Se va a programar el envío para el ${formatearFechaHoraArgentina(fechaIso!)} (hora Argentina). ¿Confirmás?`
      : 'Se va a enviar la notificación ahora mismo. ¿Confirmás?';

    this.store.confirmarAccion(mensajeConfirmacion, this.textoBoton()).then(confirmado => {
      if (!confirmado) return;

      const esFiltros = v.tipoAudiencia === 'FILTROS';
      // El contrato real pide {id, nombre}/pares con id (corrección #9) — se
      // arman acá a partir de los mismos catálogos que ya tienen cargados
      // los combos (para cuando se llega, el usuario ya interactuó con
      // ellos, así que no hay problema de timing como en la hidratación).
      const coberturas = this.catalogos.coberturas()
        .filter(c => this.coberturaIds().includes(c.id));
      const provincias = this.catalogos.provincias()
        .filter(p => this.provinciaIds().includes(p.id));
      const localidades: LocalidadNotificacion[] = this.catalogos.localidades()
        .filter(l => this.localidadIds().includes(l.id))
        .map(l => ({ provinciaId: l.provinciaId, provincia: l.provinciaNombre, localidadId: l.id, localidad: l.nombre }));

      const request: CrearNotificacionInstitucionalRequest = {
        nombreInterno: v.nombreInterno,
        titulo: v.titulo,
        body: v.mensajeCorto,
        detalle: v.mensajeAmpliado,
        tipoEntrega: v.tipoEntrega,
        fechaProgramacion: fechaIso,
        tipoAudiencia: v.tipoAudiencia,
        sexo: esFiltros ? v.sexo : null,
        provincias: esFiltros ? provincias : [],
        localidades: esFiltros ? localidades : [],
        coberturas: esFiltros ? coberturas : [],
        edadDesde: esFiltros ? v.edadDesde : null,
        edadHasta: esFiltros ? v.edadHasta : null,
        enlace: this.armarEnlace(v.enlaceTipo, v.enlaceUrl, v.enlaceSeccion, v.enlaceTextoBoton)
      };

      const resultado$ = this.modo() === 'editar' && this.id !== null
        ? this.store.editar(this.id, request)
        : this.store.crear(request);

      resultado$.subscribe(res => {
        if (res) this.router.navigate(['/notificaciones']);
      });
    });
  }

  // Para SECCION_APP se manda el id del catálogo, nunca una ruta.
  private armarEnlace(tipo: TipoEnlace, url: string, seccion: string, textoBoton: string): EnlaceNotificacion {
    if (tipo === 'SIN_ENLACE') return { tipo, destino: null, textoBoton: null };
    return {
      tipo,
      destino: tipo === 'EXTERNO' ? url.trim() : seccion,
      textoBoton: textoBoton.trim()
    };
  }

  // Solo validadores, nunca valores (ver constructor). Con emit a propósito,
  // a diferencia de actualizarValidadorFecha: si no, formStatusSig no se
  // entera y puedeGuardar queda trabado al volver a "Sin enlace".
  private actualizarValidadoresEnlace(tipo: TipoEnlace): void {
    const url = this.form.get('enlaceUrl')!;
    const seccion = this.form.get('enlaceSeccion')!;
    const textoBoton = this.form.get('enlaceTextoBoton')!;
    // URL y texto del botón: un solo validador por campo que replica las reglas
    // y mensajes del back (ver custom-validators.ts).
    url.setValidators(tipo === 'EXTERNO' ? [urlExternaValidator] : []);
    seccion.setValidators(tipo === 'SECCION_APP' ? [Validators.required] : []);
    textoBoton.setValidators(tipo !== 'SIN_ENLACE' ? [textoBotonValidator] : []);
    url.updateValueAndValidity();
    seccion.updateValueAndValidity();
    textoBoton.updateValueAndValidity();
  }

  private actualizarValidadorFecha(tipoEntrega: TipoEntrega): void {
    const ctrl = this.form.get('fechaProgramacion')!;
    if (tipoEntrega === 'PROGRAMADA') {
      ctrl.setValidators([Validators.required, fechaFuturaArgentinaValidator]);
      // Re-arma el control real por si venía de "Inmediatamente" — evita que
      // los inputs de fecha/hora queden mostrando un valor que el control no
      // tiene (ver fechaParte/horaParte). Sin marcar touched: recién se
      // vuelve a mostrar el campo, no hubo interacción todavía.
      this.actualizarFechaProgramacion(false);
    } else {
      ctrl.setValidators([]);
      ctrl.setValue('', { emitEvent: false });
    }
    ctrl.updateValueAndValidity({ emitEvent: false });
  }

  private actualizarFechaProgramacion(marcarTouched = true): void {
    const ctrl = this.form.get('fechaProgramacion')!;
    const fecha = this.fechaParte();
    const hora = this.horaParte();
    ctrl.setValue(fecha && hora ? `${fecha}T${hora}` : '');
    if (marcarTouched) {
      ctrl.markAsDirty();
      ctrl.markAsTouched();
    }
  }
}
